"use client";

import { Check, Minus } from "lucide-react";
import { ReactNode } from "react";
import { cn } from "@punchup/utils/cn";

function SelectionCheckbox({
  isSelected,
  isIndeterminate,
}: {
  isSelected: boolean;
  isIndeterminate?: boolean;
}) {
  const filled = isSelected || isIndeterminate;
  return (
    <div
      className={cn(
        "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors",
        filled
          ? "border-primary-blue bg-primary-blue"
          : "border-gray-300 bg-white"
      )}
    >
      {isIndeterminate ? (
        <Minus className="h-3.5 w-3.5 text-white" strokeWidth={3} />
      ) : (
        isSelected && (
          <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
        )
      )}
    </div>
  );
}

export interface FlatGroupSelectionListProps<TGroup> {
  groups: TGroup[];
  getGroupId: (group: TGroup) => string;
  renderGroupHeader: (group: TGroup) => ReactNode;
  /** Return all item IDs belonging to this group */
  getGroupItemIds: (group: TGroup) => string[];
  /** Return the subset of item IDs that can be toggled. If omitted, all items are enabled. */
  getEnabledItemIds?: (group: TGroup) => string[];
  isGroupDisabled?: (group: TGroup) => boolean;
  selectedItemIds: string[];
  onToggleItem: (itemId: string) => void;
  onToggleAllItems?: (
    itemIds: string[],
    select: boolean,
    groupId?: string
  ) => void;
  emptyMessage?: string;
}

export function FlatGroupSelectionList<TGroup>({
  groups,
  getGroupId,
  renderGroupHeader,
  getGroupItemIds,
  getEnabledItemIds,
  isGroupDisabled,
  selectedItemIds,
  onToggleItem,
  onToggleAllItems,
  emptyMessage = "No items found",
}: FlatGroupSelectionListProps<TGroup>) {
  if (groups.length === 0) {
    return (
      <div className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => {
        const groupId = getGroupId(group);
        const allItemIds = getGroupItemIds(group);
        const enabledItemIds = getEnabledItemIds
          ? getEnabledItemIds(group)
          : allItemIds;
        const groupDisabled = !!isGroupDisabled?.(group);

        // Single-item group: flat row toggling the single item
        if (allItemIds.length === 1) {
          const itemId = allItemIds[0];
          const isSelected = selectedItemIds.includes(itemId);
          const disabled = groupDisabled || !enabledItemIds.includes(itemId);

          return (
            <button
              key={groupId}
              disabled={disabled}
              onClick={() => {
                if (!disabled) onToggleItem(itemId);
              }}
              className={cn(
                "flex w-full items-center gap-3 rounded-lg border border-gray-200 px-3 md:px-4 py-3 text-left transition-colors dark:border-gray-700",
                disabled
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-50 dark:hover:bg-gray-700/50",
                isSelected && !disabled && "bg-gray-100 dark:bg-gray-700"
              )}
            >
              <SelectionCheckbox isSelected={isSelected} />
              {renderGroupHeader(group)}
            </button>
          );
        }

        // Multi-item group: row toggles all enabled items
        const allEnabledDisabled = enabledItemIds.length === 0;
        const headerDisabled = groupDisabled || allEnabledDisabled;
        const hasSelectedItems = allItemIds.some((id) =>
          selectedItemIds.includes(id)
        );
        const allSelected =
          enabledItemIds.length > 0 &&
          enabledItemIds.every((id) => selectedItemIds.includes(id));

        const handleRowClick = () => {
          if (headerDisabled) return;
          if (onToggleAllItems) {
            onToggleAllItems(enabledItemIds, !allSelected, groupId);
          } else {
            const shouldSelect = !allSelected;
            enabledItemIds.forEach((id) => {
              const currentlySelected = selectedItemIds.includes(id);
              if (shouldSelect && !currentlySelected) {
                onToggleItem(id);
              } else if (!shouldSelect && currentlySelected) {
                onToggleItem(id);
              }
            });
          }
        };

        return (
          <button
            key={groupId}
            disabled={headerDisabled}
            onClick={handleRowClick}
            className={cn(
              "flex w-full items-center gap-3 rounded-lg border border-gray-200 px-3 md:px-4 py-3 text-left transition-colors dark:border-gray-700",
              headerDisabled
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-50 dark:hover:bg-gray-700/50",
              hasSelectedItems &&
                !headerDisabled &&
                "bg-gray-100 dark:bg-gray-700"
            )}
          >
            <SelectionCheckbox
              isSelected={allSelected}
              isIndeterminate={hasSelectedItems && !allSelected}
            />
            {renderGroupHeader(group)}
          </button>
        );
      })}
    </div>
  );
}
